import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../../types/base-tool-types';
import {recommendationStatuses} from '@packages/domains/recommendations/data-types';

const toolName = 'update-site-recommendations' as const;

const collectionRefSchema = z
  .object({
    id: z.string().describe('The CMS collection ID'),
    itemId: z.string().optional().describe('The CMS collection item ID'),
    fieldSlug: z.string().optional().describe('The CMS collection field slug'),
  })
  .describe(
    'Link this recommendation to a CMS collection and optionally a specific item'
  );

const updateItemSchema = z.object({
  recommendationId: z.string().describe('The recommendation ID to update'),
  status: z
    .enum([...recommendationStatuses])
    .optional()
    .describe('New status for the recommendation'),
  action: z
    .string()
    .optional()
    .describe('Description of the action taken or to be taken'),
  proposal: z
    .record(z.unknown())
    .optional()
    .describe('Updated proposed fix as a key-value object'),
  errorMessage: z
    .string()
    .optional()
    .describe(
      'Error message describing why the recommendation could not be applied'
    ),
  collection: collectionRefSchema.optional(),
});

export type UpdateRecommendationItem = z.infer<typeof updateItemSchema>;

export const updateSiteRecommendationsArgsSchema = z.object({
  siteId: z.string().describe('The site ID the recommendations belong to'),
  items: z
    .array(updateItemSchema)
    .describe('Array of recommendation updates to apply'),
});

export type UpdateSiteRecommendationsArgs = z.infer<
  typeof updateSiteRecommendationsArgsSchema
>;

export const updateSiteRecommendationsResultSchema = z.object({
  updatedCount: z.number(),
  errors: z.array(z.string()),
});

export type UpdateSiteRecommendationsResult = z.infer<
  typeof updateSiteRecommendationsResultSchema
>;

export const updateSiteRecommendationsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Update site recommendations',
  family: 'recommendations',
  prettyStatus: {
    executed: 'Updated site recommendations',
    executing: 'Updating site recommendations',
    failed: 'Failed to update site recommendations',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Updates existing site recommendations. Supports updating status, action,
      proposal, errorMessage, and collection fields. Each item targets a specific recommendation by ID.
      The updatedOn timestamp is set automatically.
    `,
    inputSchema: updateSiteRecommendationsArgsSchema,
  },
  type: 'server',
  permission: [
    {
      resource: 'SiteAeoRecommendations',
      action: 'manage',
      resolveResources: (args) => [
        {type: 'SiteAeoRecommendations', id: args.siteId},
      ],
    },
  ],
  confirmBeforeExecuting: false,
  timeout: 60000,
});
